import { supabase } from '../supabase';

/* ============================================
   CONSTANTS
   ============================================ */

export const DEFAULT_CATEGORIES = [
  { name: 'Food',          icon: 'utensils',  color: '#ff7466' },
  { name: 'Transport',     icon: 'car',       color: '#0d4f5c' },
  { name: 'Bills',         icon: 'receipt',   color: '#8b2c2c' },
  { name: 'Entertainment', icon: 'music',     color: '#b8870c' },
  { name: 'Health',        icon: 'heart',     color: '#7a9b76' },
  { name: 'Shopping',      icon: 'bag',       color: '#2d5a3f' },
  { name: 'Salary',        icon: 'briefcase', color: '#2d5a3f' },
  { name: 'Other',         icon: 'circle',    color: '#6b4f3a' },
];

export const CATEGORY_BY_NAME = DEFAULT_CATEGORIES.reduce((acc, c) => {
  acc[c.name] = c;
  return acc;
}, {});

export const TYPE_LABELS = {
  income:  'Income',
  expense: 'Expense',
};

export const BUDGET_STATUS = {
  SAFE:    'safe',
  WARNING: 'warning',
  OVER:    'over',
};

/* ============================================
   PROMPT — money insight
   ============================================ */

export const MONEY_INSIGHT_PROMPT = ({ transactions, budgets, currentMonth, totals, budgetProgress }) => `You are the Money module of Curx AI, a calm and grounded coach helping the user understand their finances without anxiety.

Read the user's transactions for ${currentMonth} and their budget targets. Identify 2-3 specific patterns and 2-3 actionable suggestions. Be concrete — reference actual amounts and categories.

CURRENT MONTH: ${currentMonth}
INCOME this month: £${totals.income.toFixed(2)}
EXPENSES this month: £${totals.expenses.toFixed(2)}
NET: £${totals.net.toFixed(2)}

SPENDING BY CATEGORY:
${Object.entries(totals.by_category)
  .filter(([_, v]) => v.expense > 0)
  .sort((a, b) => b[1].expense - a[1].expense)
  .map(([cat, v]) => `  - ${cat}: £${v.expense.toFixed(2)}`)
  .join('\n') || '  (no expenses logged)'}

BUDGET TARGETS:
${budgets.length === 0 ? '  (no budgets set)' : budgets.map(b => `  - ${b.category}: £${Number(b.monthly_limit).toFixed(2)}/month`).join('\n')}

BUDGET PROGRESS:
${budgetProgress.length === 0 ? '  (no progress to report)' : budgetProgress.map(p => `  - ${p.category}: £${p.spent.toFixed(2)} of £${p.limit.toFixed(2)} (${p.percent}% — ${p.status})`).join('\n')}

RECENT TRANSACTIONS (last 15):
${transactions.slice(0, 15).map(t => `  [${t.date}] ${t.log_type === 'income' ? '+' : '-'}£${Number(t.amount).toFixed(2)} · ${t.category} · ${t.description || '(no description)'}`).join('\n') || '  (no transactions)'}

Output ONLY valid JSON, no markdown:

{
  "alert": "<one-line summary of overall budget health: 'on track' / 'watch out for X' / 'over budget on Y' — or empty string>",
  "observations": ["<1-sentence pattern 1, with specific numbers>", "<pattern 2>", "<pattern 3>"],
  "suggestions": ["<actionable suggestion 1, specific>", "<suggestion 2>", "<suggestion 3>"]
}

Be specific with amounts. Reference categories by name. No platitudes. No moralizing.`;

/* ============================================
   SUPABASE — READ
   ============================================ */

export async function loadTransactionsForMonth(userId, year, month) {
  if (!userId) return [];
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  // Last day of month
  const endDate = new Date(year, month, 0); // month is 1-based, day 0 = last day of prev month
  const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
  const { data, error } = await supabase
    .from('money_logs')
    .select('*')
    .eq('user_id', userId)
    .gte('date', startDate)
    .lte('date', endStr)
    .order('date', { ascending: false })
    .order('logged_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function loadRecentTransactions(userId, limit = 20) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('money_logs')
    .select('*')
    .eq('user_id', userId)
    .order('logged_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function loadAllTransactionsInRange(userId, startDate, endDate) {
  if (!userId) return [];
  let query = supabase
    .from('money_logs')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .order('logged_at', { ascending: false });
  if (startDate) query = query.gte('date', startDate);
  if (endDate)   query = query.lte('date', endDate);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function loadBudgetCategories(userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('budget_categories')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('category', { ascending: true });
  if (error) throw error;
  return data || [];
}

/* ============================================
   SUPABASE — WRITE (with .select() RLS verification)
   ============================================ */

export async function logTransaction({ user_id, log_type, amount, category, description, date, notes }) {
  if (!user_id) throw new Error('Missing user_id');
  const amt = Math.abs(Number(amount));
  if (!amt || isNaN(amt)) throw new Error('Amount must be a positive number');
  const payload = {
    user_id,
    date: date || todayLocalDateStr(),
    log_type,
    amount: amt,
    currency: 'GBP',
    category: String(category || 'Other').trim(),
    description: description ? String(description).trim() : null,
    notes: notes ? String(notes).trim() : null,
  };
  const { data, error } = await supabase
    .from('money_logs')
    .insert(payload)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your RLS policy for INSERT on money_logs.");
  }
  return data[0];
}

export async function deleteTransaction(transactionId) {
  if (!transactionId) throw new Error('Missing transaction id');
  const { data, error } = await supabase
    .from('money_logs')
    .delete()
    .eq('id', transactionId)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Delete didn't affect any rows. Check your RLS policy for DELETE on money_logs.");
  }
}

export async function upsertBudget({ user_id, category, monthly_limit }) {
  if (!user_id) throw new Error('Missing user_id');
  const limit = Number(monthly_limit);
  if (!limit || isNaN(limit) || limit <= 0) throw new Error('Limit must be a positive number');
  const payload = {
    user_id,
    category: String(category).trim(),
    monthly_limit: limit,
    currency: 'GBP',
    is_active: true,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('budget_categories')
    .upsert(payload, { onConflict: 'user_id,category' })
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Save didn't write any rows. Check your RLS policy for INSERT/UPDATE on budget_categories.");
  }
  return data[0];
}

export async function deleteBudget(userId, category) {
  if (!userId || !category) throw new Error('Missing user_id or category');
  const { data, error } = await supabase
    .from('budget_categories')
    .delete()
    .eq('user_id', userId)
    .eq('category', category)
    .select();
  if (error) throw error;
  // No rows means there was no budget to delete — that's OK, not an error
  return data || [];
}

/* ============================================
   AGGREGATIONS — pure functions
   ============================================ */

export function getMonthlyTotals(transactions) {
  let income = 0;
  let expenses = 0;
  const by_category = {};
  for (const t of (transactions || [])) {
    const amt = Number(t.amount) || 0;
    const cat = t.category || 'Other';
    if (!by_category[cat]) by_category[cat] = { income: 0, expense: 0 };
    if (t.log_type === 'income') {
      income += amt;
      by_category[cat].income += amt;
    } else if (t.log_type === 'expense') {
      expenses += amt;
      by_category[cat].expense += amt;
    }
  }
  return {
    income,
    expenses,
    net: income - expenses,
    by_category,
  };
}

export function getBudgetProgress(transactions, budgets) {
  const totals = getMonthlyTotals(transactions);
  return (budgets || []).map(b => {
    const spent = totals.by_category[b.category]?.expense || 0;
    const limit = Number(b.monthly_limit) || 0;
    const percent = limit > 0 ? Math.round((spent / limit) * 100) : 0;
    let status = BUDGET_STATUS.SAFE;
    if (percent > 90)      status = BUDGET_STATUS.OVER;
    else if (percent >= 70) status = BUDGET_STATUS.WARNING;
    return {
      category: b.category,
      spent,
      limit,
      percent,
      status,
      over_amount: spent > limit ? spent - limit : 0,
    };
  });
}

// Cross-tab: compact summary for Today's AI prompt. Returns null if there's
// nothing meaningful to share (no transactions yet this month).
export async function getMonthSummaryForToday(userId) {
  try {
    if (!userId) return null;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const [transactions, budgets] = await Promise.all([
      loadTransactionsForMonth(userId, year, month).catch(() => []),
      loadBudgetCategories(userId).catch(() => []),
    ]);
    if (!transactions || transactions.length === 0) return null;
    const totals = getMonthlyTotals(transactions);
    const budget_total = (budgets || []).reduce((sum, b) => sum + (Number(b.monthly_limit) || 0), 0);

    // Top spending category by expense
    let top_category = null;
    let top_amount = 0;
    for (const [cat, v] of Object.entries(totals.by_category)) {
      if (v.expense > top_amount) { top_amount = v.expense; top_category = cat; }
    }

    // Status: only meaningful if budget_total > 0
    let status = null;
    if (budget_total > 0) {
      const pct = (totals.expenses / budget_total) * 100;
      if (pct >= 90) status = 'over';
      else if (pct >= 70) status = 'watch';
      else status = 'on track';
    }

    return {
      spent_total: totals.expenses,
      budget_total,
      top_category,
      status,
    };
  } catch (_) {
    return null;
  }
}

/* ============================================
   AI INSIGHT
   ============================================ */

export async function generateMoneyInsight({ transactions, budgets, currentMonth }) {
  const totals = getMonthlyTotals(transactions);
  const budgetProgress = getBudgetProgress(transactions, budgets);
  const prompt = MONEY_INSIGHT_PROMPT({
    transactions: transactions || [],
    budgets: budgets || [],
    currentMonth,
    totals,
    budgetProgress,
  });
  const response = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model: 'sonnet' }),
  });
  const data = await response.json();
  const text = (data?.content || []).map(i => i.text || '').join('').trim();
  const parsed = tryParseJSONObject(text);
  if (!parsed || !Array.isArray(parsed.observations) || !Array.isArray(parsed.suggestions)) {
    throw new Error('Money insight returned invalid shape');
  }
  return {
    alert: String(parsed.alert || '').trim(),
    observations: parsed.observations.map(s => String(s).trim()).filter(Boolean).slice(0, 3),
    suggestions:  parsed.suggestions.map(s => String(s).trim()).filter(Boolean).slice(0, 3),
  };
}

/* ============================================
   FORMATTERS / UTILS
   ============================================ */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function formatGBP(amount) {
  const n = Number(amount) || 0;
  const sign = n < 0 ? '-' : '';
  return `${sign}£${Math.abs(n).toFixed(2)}`;
}

export function formatMonthLabel(year, month) {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

export function formatTransactionDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
}

export function formatTransactionDateLong(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function todayLocalDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function getCurrentMonth() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function getPreviousMonth(year, month) {
  if (month === 1) return { year: year - 1, month: 12 };
  return { year, month: month - 1 };
}

export function getNextMonth(year, month) {
  if (month === 12) return { year: year + 1, month: 1 };
  return { year, month: month + 1 };
}

export function isCurrentOrFutureMonth(year, month) {
  const cur = getCurrentMonth();
  return year > cur.year || (year === cur.year && month >= cur.month);
}

export function transactionsToCsv(transactions) {
  const header = ['Date', 'Type', 'Amount', 'Currency', 'Category', 'Description', 'Notes'];
  const rows = (transactions || []).map(t => [
    t.date,
    t.log_type,
    Number(t.amount).toFixed(2),
    t.currency || 'GBP',
    t.category || '',
    (t.description || '').replace(/"/g, '""'),
    (t.notes || '').replace(/"/g, '""'),
  ]);
  const escape = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  return [header, ...rows].map(r => r.map(escape).join(',')).join('\n');
}

function tryParseJSONObject(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch (_) { /* fallthrough */ }
  const match = String(text).match(/\{[\s\S]*\}/);
  if (match) {
    try { return JSON.parse(match[0]); } catch (__) { /* fallthrough */ }
  }
  return null;
}
